import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Play, Music, Shield, Users, Zap, 
  FileAudio, FileText, Download, CheckCircle,
  ArrowRight, Star, Crown, Headphones, Globe,
  Eye, Target, Sparkles, Rocket, Image, BarChart3,
  ShoppingCart, Search, Send, ChevronRight, Package, ListMusic
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import TrackCard from '../components/TrackCard';
import FeaturedArtists from '../components/FeaturedArtists';
import OfficialPresence from '../components/OfficialPresence';
import CommunityStats from '../components/CommunityStats';
import PlaylistLiveCounter from '../components/PlaylistLiveCounter';
import { SpotifySection } from '../components/SpotifyPlayer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const isEs = language === 'es';
  const [tracks, setTracks] = useState([]);
  const [packs, setPacks] = useState([]);
  const [news, setNews] = useState([]);
  const [promoPlans, setPromoPlans] = useState([]);
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [heroQuery, setHeroQuery] = useState('');
  const [heroResponse, setHeroResponse] = useState(null);
  const [heroLoading, setHeroLoading] = useState(false);
  const heroSessionId = useState(() => 'hero_' + Date.now())[0];
  const [promoPopup, setPromoPopup] = useState(null);
  const [showPromoPopup, setShowPromoPopup] = useState(false);

  useEffect(() => {
    fetchData();
    // Fetch active promotion for popup
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/active-promotion`);
        if (r.data.active) {
          const dismissed = sessionStorage.getItem('promo_dismissed');
          if (!dismissed) {
            setPromoPopup(r.data);
            setTimeout(() => setShowPromoPopup(true), 2000);
          }
        }
      } catch {}
    })();
  }, []);

  const fetchData = async () => {
    try {
      const [tracksRes, packsRes, newsRes, promoRes] = await Promise.all([
        axios.get(`${API_URL}/api/tracks/?limit=8`),
        axios.get(`${API_URL}/api/packs/`),
        axios.get(`${API_URL}/api/news/?limit=3`),
        axios.get(`${API_URL}/api/playlist-promo/plans`),
      ]);
      setTracks(tracksRes.data);
      setPacks(packsRes.data);
      setNews(newsRes.data);
      setPromoPlans(promoRes.data?.plans || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleHeroSearch = async (e) => {
    e.preventDefault();
    if (!heroQuery.trim() || heroLoading) return;
    setHeroLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/ai/hero-search`, {
        message: heroQuery,
        session_id: heroSessionId
      });
      setHeroResponse(res.data);
    } catch {
      setHeroResponse({ response: 'Explora nuestro catálogo para encontrar música profesional lista para lanzar.', products: [] });
    }
    setHeroLoading(false);
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/subscribe`, { email });
      setSubscribeStatus('success');
      setEmail('');
    } catch (error) {
      setSubscribeStatus('error');
    }
  };

  const licenses = [
    {
      id: 'basic',
      name: t('licenses.basic.name'),
      price: 0,
      features: t('licenses.basic.features'),
      included: true
    },
    {
      id: 'exclusive',
      name: t('licenses.exclusive.name'),
      price: 99.99,
      features: t('licenses.exclusive.features'),
      popular: true
    }
  ];

  const membershipTiers = [
    {
      id: 'og-access',
      name: t('membership.tiers.access.name'),
      price: 14.99,
      downloads: 100,
      features: t('membership.tiers.access.features'),
      bestEntry: true
    },
    {
      id: 'og-pro',
      name: t('membership.tiers.pro.name'),
      price: 35.99,
      downloads: 200,
      features: t('membership.tiers.pro.features'),
      popular: true
    },
    {
      id: 'og-unlimited',
      name: t('membership.tiers.unlimited.name'),
      price: 99,
      downloads: -1,
      features: t('membership.tiers.unlimited.features'),
      exclusive: true
    }
  ];

  const services = [
    {
      id: 'production',
      name: t('services.production.name'),
      description: t('services.production.desc'),
      price: 250,
      image: 'https://images.pexels.com/photos/3990847/pexels-photo-3990847.jpeg'
    },
    {
      id: 'custom-tracks',
      name: t('services.customTracks.name'),
      description: t('services.customTracks.desc'),
      price: 25,
      image: 'https://images.unsplash.com/photo-1552174588-6733961c358e'
    },
    {
      id: 'sound-branding',
      name: t('services.soundBranding.name'),
      description: t('services.soundBranding.desc'),
      price: 300,
      image: 'https://images.unsplash.com/photo-1550520115-e9b95e845e4c'
    }
  ];

  const deliveryItems = [
    { icon: FileAudio, title: t('delivery.items.wav.title'), desc: t('delivery.items.wav.desc') },
    { icon: Music, title: t('delivery.items.mp3.title'), desc: t('delivery.items.mp3.desc') },
    { icon: FileText, title: t('delivery.items.license.title'), desc: t('delivery.items.license.desc') },
    { icon: Download, title: t('delivery.items.metadata.title'), desc: t('delivery.items.metadata.desc') }
  ];

  return (
    <div className="min-h-screen">
      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-start md:items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/54d51791-75da-4508-8099-24af4d7cd2f0/images/d3890fe60f71c5db5071aa193169c419f02839c9eddc09e5c10e35d0f5e36223.png"
            alt="Hero Background"
            className="w-full h-full object-cover animate-slow-zoom"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050507]/60 via-[#050507]/80 to-[#050507]" />
          <div className="absolute inset-0 luxury-mesh" />
        </div>

        <div className="relative z-10 w-full pt-40 md:pt-20">
          <div className="container-custom text-center pb-20">
          <div className="max-w-4xl mx-auto">
            <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.25em] sm:tracking-[0.3em] text-white/50 mb-6 animate-fade-in-up">
              {t('hero.overline')}
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] mb-6 animate-fade-in-up delay-100">
              {t('hero.title')}
              <br />
              <span className="gold-text">{t('hero.titleHighlight')}</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-white/65 leading-relaxed mb-8 max-w-2xl mx-auto animate-fade-in-up delay-200 px-2">
              {t('hero.subtitle')}
            </p>

            {/* COMMUNITY STATS BAR — Social Proof.
                Positioned DIRECTLY below the hero subtitle on ALL viewports
                (user request — avoid covering text on mobile) */}
            <div className="mb-10 animate-fade-in-up delay-200" data-testid="hero-stats-wrapper">
              <CommunityStats />
            </div>

            {/* Triple-Pillar Pitch — TRACKS + PACKS + PROMOTION (the 3 things we sell) */}
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-3 max-w-5xl mx-auto animate-fade-in-up delay-300" data-testid="hero-triple-pillar">
              <Link to="/catalog" className="group glass-card p-5 hover:border-[#E8720C]/40 transition-all text-left flex items-center gap-4 hover:-translate-y-1">
                <div className="w-12 h-12 rounded-xl bg-[#E8720C]/15 border border-[#E8720C]/30 flex items-center justify-center shrink-0">
                  <Music className="w-5 h-5 text-[#E8720C]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#E8720C] mb-0.5">{isEs ? '1. Tracks Sueltos' : '1. Single Tracks'}</p>
                  <p className="text-xs text-white/60 leading-snug">{isEs ? 'Compra desde $5.99 · Listo para Spotify' : 'From $5.99 · Spotify-ready'}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-white/30 group-hover:text-[#E8720C] group-hover:translate-x-1 transition-all" />
              </Link>

              <Link to="/packs" className="group glass-card p-5 border-[#E8720C]/30 hover:border-[#E8720C]/60 ring-1 ring-[#E8720C]/10 transition-all text-left flex items-center gap-4 hover:-translate-y-1 relative bg-gradient-to-br from-[#E8720C]/8 to-transparent" data-testid="hero-pillar-packs">
                <span className="absolute -top-2 right-3 px-2 py-0.5 text-[8px] font-bold uppercase tracking-[0.15em] bg-[#E8720C] text-white rounded-full animate-pulse">HOT</span>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E8720C] to-[#C55A00] flex items-center justify-center shrink-0">
                  <Package className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#E8720C] mb-0.5">{isEs ? '2. Packs · Ahorra 40%' : '2. Packs · Save 40%'}</p>
                  <p className="text-xs text-white/60 leading-snug">{isEs ? '5, 10 o 20 tracks · Mejor precio por canción' : '5, 10 or 20 tracks · Best per-song price'}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-white/30 group-hover:text-[#E8720C] group-hover:translate-x-1 transition-all" />
              </Link>

              <Link to="/playlist-promotion" className="group glass-card p-5 hover:border-green-500/40 transition-all text-left flex items-center gap-4 hover:-translate-y-1 relative" data-testid="hero-pillar-promo">
                <span className="absolute -top-2 right-3 px-2 py-0.5 text-[8px] font-bold uppercase tracking-[0.15em] bg-green-500 text-white rounded-full">NEW</span>
                <div className="w-12 h-12 rounded-xl bg-green-500/15 border border-green-500/30 flex items-center justify-center shrink-0">
                  <ListMusic className="w-5 h-5 text-green-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-green-400 mb-0.5">{isEs ? '3. Promociona tu Música' : '3. Promote Your Music'}</p>
                  <p className="text-xs text-white/60 leading-snug">{isEs ? 'Tu track en nuestras +100 playlists' : 'Your track on +100 playlists'}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-white/30 group-hover:text-green-400 group-hover:translate-x-1 transition-all" />
              </Link>
            </div>

            {/* Hero AI Search */}
            <div className="mt-10 max-w-2xl mx-auto animate-fade-in-up delay-400" data-testid="hero-ai-search">
              <form onSubmit={handleHeroSearch} className="relative group">
                <div className="absolute -inset-[1px] bg-gradient-to-r from-[#E8720C]/40 via-[#E8720C]/10 to-[#E8720C]/40 rounded-2xl blur-sm group-focus-within:from-[#E8720C]/60 group-focus-within:to-[#E8720C]/60 transition-all" />
                <div className="relative flex items-center bg-[#0c0c10]/90 border border-white/10 rounded-2xl overflow-hidden group-focus-within:border-[#E8720C]/30">
                  <Sparkles className="w-5 h-5 text-[#E8720C] ml-5 shrink-0" />
                  <input
                    type="text"
                    value={heroQuery}
                    onChange={(e) => setHeroQuery(e.target.value)}
                    placeholder={t('hero.aiPlaceholder') || '¿Qué tipo de música necesitas? Yo te ayudo.'}
                    className="flex-1 px-4 py-4 bg-transparent text-white text-sm placeholder-white/35 focus:outline-none"
                    data-testid="hero-ai-input"
                  />
                  <button
                    type="submit"
                    disabled={heroLoading || !heroQuery.trim()}
                    className="mr-2 px-5 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-40 flex items-center gap-2"
                    data-testid="hero-ai-submit"
                  >
                    {heroLoading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <><Send className="w-4 h-4" />{t('hero.aiSend') || 'Enviar'}</>
                    )}
                  </button>
                </div>
              </form>

              {/* AI Response */}
              {heroResponse && (
                <div className="mt-4 glass-card p-5 text-left animate-fade-in-up" data-testid="hero-ai-response">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-[#E8720C]/20 flex items-center justify-center shrink-0">
                      <Sparkles className="w-4 h-4 text-[#E8720C]" />
                    </div>
                    <p className="text-sm text-white/80 leading-relaxed">{heroResponse.response}</p>
                  </div>
                  {heroResponse.products && heroResponse.products.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-white/5">
                      <p className="text-xs text-white/40 mb-2">{t('hero.aiRecommended') || 'Recomendados para ti:'}</p>
                      <div className="flex flex-wrap gap-2">
                        {heroResponse.products.slice(0, 4).map((p, i) => (
                          <Link
                            key={i}
                            to="/catalog"
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/5 hover:bg-[#E8720C]/10 border border-white/10 hover:border-[#E8720C]/30 rounded-lg text-xs text-white/70 hover:text-white transition-all"
                          >
                            <Music className="w-3 h-3 text-[#E8720C]" />
                            <span>{p.title}</span>
                            <span className="text-[#E8720C] font-semibold">${p.price}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/20 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* PROMOTIONAL BANNER removed (Feb 2026) — now Packs section is the main visual driver */}

      {/* ECOSYSTEM SECTION */}
      <section id="about" className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-4">
              {t('ecosystem.overline')}
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
              {t('ecosystem.title')}
              <br />
              <span className="gold-text">{t('ecosystem.titleHighlight')}</span>
            </h2>
            <p className="text-lg text-white/60 max-w-2xl mx-auto">
              {t('ecosystem.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Music, ...t('ecosystem.cards.tracks') },
              { icon: Shield, ...t('ecosystem.cards.licensing') },
              { icon: Users, ...t('ecosystem.cards.membership') },
              { icon: Zap, ...t('ecosystem.cards.promotion') }
            ].map((item, index) => (
              <div 
                key={index}
                className="glass-card p-8 text-center hover:-translate-y-1 transition-transform"
                data-testid={`ecosystem-card-${index}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center mx-auto mb-5">
                  <item.icon className="w-6 h-6 text-[#E8720C]" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-white/50">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPOTIFY LISTENING SECTION */}
      <SpotifySection />

      {/* MUSIC CATALOG SECTION */}
      <section id="catalog" className="section-padding bg-gradient-to-b from-transparent via-[#0a0a0c] to-transparent">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E8720C]/15 ring-1 ring-[#E8720C]/30 mb-4">
                <span className="text-[10px] font-bold tracking-[0.15em] text-[#E8720C]">PRODUCTO 1 DE 3</span>
              </div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8720C] mb-4">
                Tracks Sueltos · Listos para Distribuir
              </p>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
                Compra Música <span className="gold-text">Pre-Masterizada</span>
              </h2>
              <p className="text-white/50 mt-2 max-w-xl">Cada track ya viene masterizado nivel estudio. WAV + MP3 + portada exclusiva + licencia comercial certificada. Listo para Spotify desde el primer clic.</p>
            </div>
            <Link 
              to="/catalog" 
              className="mt-6 md:mt-0 flex items-center gap-2 text-[#E8720C] hover:text-white transition-colors font-semibold"
              data-testid="view-all-tracks"
            >
              Explorar Catalogo
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Ecosystem CTA Banners */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Main Ecosystem Banner */}
            <Link to="/catalog" className="glass-card p-8 border border-[#E8720C]/10 bg-gradient-to-br from-[#E8720C]/5 to-transparent hover:border-[#E8720C]/30 transition-all group" data-testid="ecosystem-cta-banner">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#E8720C] mb-3">Al Comprar tu Musica Recibes</p>
              <div className="space-y-3 mb-6">
                {[
                  { label: '✓ Música YA Masterizada Pro', desc: 'Cada track sale de estudio · Listo para Spotify, Apple Music y radio. Sin retoques.', highlight: true },
                  { label: 'Música en WAV + MP3', desc: '2 formatos de alta calidad, listos para streaming' },
                  { label: 'Portada OG Exclusiva', desc: 'Una portada unica creada solo para ti' },
                  { label: 'Licencia Certificada', desc: 'Documento PDF con tus derechos comerciales' },
                  { label: 'Guia de Distribucion', desc: 'Paso a paso para llegar a Spotify, Apple Music y mas', link: '/guia-distribucion' },
                  { label: 'Acceso Ecosistema Playlists', desc: 'Tu musica en nuestras playlists principales (tracks seleccionados)' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${item.highlight ? 'bg-green-500/20 ring-1 ring-green-500/40' : 'bg-[#E8720C]/10'}`}>
                      <span className={`text-xs font-bold ${item.highlight ? 'text-green-400' : 'text-[#E8720C]'}`}>{item.highlight ? '✓' : i}</span>
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${item.highlight ? 'text-green-300' : ''}`}>{item.label}</p>
                      <p className="text-xs text-white/40">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 text-[#E8720C] text-sm font-semibold group-hover:gap-3 transition-all">
                Ver Catalogo Completo <ArrowRight className="w-4 h-4" />
              </div>
            </Link>

            {/* Plans Banner */}
            <Link to="/memberships" className="glass-card p-8 border border-white/5 hover:border-[#E8720C]/20 transition-all group" data-testid="plans-cta-banner">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/50 mb-3">Planes de Membresia</p>
              <h3 className="text-2xl font-bold mb-2">Descarga sin Limites</h3>
              <p className="text-sm text-white/50 mb-6">Accede a todo el catalogo con un plan mensual. Desde $14.99/mes.</p>
              <div className="space-y-2 mb-6">
                {[
                  { plan: 'OG Access', price: '$14.99/mes', desc: '100 descargas' },
                  { plan: 'OG Pro', price: '$35.99/mes', desc: '200 descargas' },
                  { plan: 'OG Unlimited', price: '$99/mes', desc: 'Descargas ilimitadas' },
                ].map((p, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-white/5">
                    <div>
                      <span className="text-sm font-medium">{p.plan}</span>
                      <span className="text-xs text-white/40 ml-2">{p.desc}</span>
                    </div>
                    <span className="text-sm font-bold text-[#E8720C]">{p.price}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 text-[#E8720C] text-sm font-semibold group-hover:gap-3 transition-all">
                Ver Todos los Planes <ArrowRight className="w-4 h-4" />
              </div>
            </Link>

            {/* Packs CTA Banner — protagonist */}
            <Link to="/packs" className="glass-card p-8 border border-[#E8720C]/20 bg-gradient-to-br from-[#E8720C]/10 via-orange-500/5 to-transparent hover:border-[#E8720C]/50 transition-all group relative overflow-hidden" data-testid="packs-cta-banner">
              <div className="absolute top-4 right-4">
                <span className="px-2 py-0.5 bg-[#E8720C] text-white text-[10px] font-bold rounded uppercase tracking-wider animate-pulse">{isEs ? 'Ahorra 40%' : 'Save 40%'}</span>
              </div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#E8720C] mb-3">{isEs ? 'Music Packs · Listos para Lanzar' : 'Music Packs · Ready to Release'}</p>
              <h3 className="text-2xl font-bold mb-1">{isEs ? 'Ahorra Comprando en Pack' : 'Save Buying in Pack'}</h3>
              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 mb-3 rounded-full bg-green-500/15 ring-1 ring-green-500/30 text-green-300 text-[10px] font-bold uppercase tracking-wider">
                <CheckCircle className="w-3 h-3" />
                {isEs ? 'Todo Pre-Masterizado Pro' : 'All Pro-Mastered'}
              </div>
              <p className="text-sm text-white/50 mb-6">{isEs ? 'Cada pack incluye múltiples tracks YA masterizados nivel estudio: WAV + MP3 + Portada 3000×3000 + Licencia automática. Compra → Descarga → Distribuye.' : 'Each pack includes multiple ALREADY studio-mastered tracks: WAV + MP3 + Cover 3000×3000 + Auto-license. Buy → Download → Distribute.'}</p>
              <div className="space-y-2 mb-6">
                {[
                  { plan: isEs ? 'Track Único' : 'Single Track',     price: '$5.99',  desc: isEs ? '1 canción · WAV + MP3 + Portada' : '1 track · WAV + MP3 + Cover' },
                  { plan: 'Pack 5',                                  price: '$24.99', desc: isEs ? '5 canciones · $5.00 c/u' : '5 tracks · $5.00 each' },
                  { plan: 'Pack 10',                                 price: '$39.99', desc: isEs ? '10 canciones · $4.00 c/u' : '10 tracks · $4.00 each', highlight: true },
                  { plan: 'Pack 20',                                 price: '$59.99', desc: isEs ? '20 canciones · $3.00 c/u' : '20 tracks · $3.00 each' },
                ].map((p, i) => (
                  <div key={i} className={`flex items-center justify-between py-2 border-b border-white/5 ${p.highlight ? 'border-[#E8720C]/20' : ''}`}>
                    <div>
                      <span className={`text-sm font-medium ${p.highlight ? 'text-[#E8720C]' : ''}`}>
                        {p.plan}
                        {p.highlight && <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wider">{isEs ? 'Más popular' : 'Most popular'}</span>}
                      </span>
                      <span className="text-xs text-white/40 ml-2">{p.desc}</span>
                    </div>
                    <span className="text-sm font-bold text-[#E8720C]">{p.price}</span>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-[#E8720C]/80 font-semibold mb-2 uppercase tracking-wider">{isEs ? 'Compra → Descarga → Lanza' : 'Buy → Download → Release'}</p>
              <div className="flex items-center gap-2 text-[#E8720C] text-sm font-semibold group-hover:gap-3 transition-all">
                {isEs ? 'Ver todos los Packs' : 'See all Packs'} <ArrowRight className="w-4 h-4" />
              </div>
            </Link>
          </div>

          {/* Track list preview */}
          <div className="glass-card overflow-hidden">
            <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
              <p className="text-xs text-white/40 font-semibold uppercase tracking-wider">Ultimos Tracks Agregados</p>
              <Link to="/catalog" className="text-xs text-[#E8720C] hover:underline">Ver todos</Link>
            </div>
            {loading ? (
              <div className="p-4 space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-10 bg-white/[0.03] rounded animate-pulse" />)}</div>
            ) : (
              <div>
                {tracks.slice(0, 6).map((track, idx) => (
                  <Link key={track.id} to="/catalog"
                    className="flex items-center gap-4 px-5 py-3 hover:bg-white/[0.03] transition-colors border-b border-white/[0.02]"
                    data-testid={`home-track-${track.id}`}>
                    <span className="text-xs text-white/20 w-5">{idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{track.title}</p>
                      <p className="text-[11px] text-white/40">{track.artist || 'OG MUSIC'} {track.genre && `· ${track.genre}`}</p>
                    </div>
                    <span className="text-xs text-white/30">{track.bpm ? `${track.bpm} BPM` : ''}</span>
                    <span className="text-sm font-semibold text-[#E8720C]">${track.price}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* PLAYLIST PROMOTION SECTION — replaces the removed standalone Licenses section */}
      <section id="playlist-promotion" className="section-padding">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/15 ring-1 ring-green-500/30 mb-4">
              <span className="text-[10px] font-bold tracking-[0.15em] text-green-400">PRODUCTO 3 DE 3</span>
              <span className="px-1.5 py-0.5 bg-green-500 text-white text-[8px] font-bold rounded uppercase tracking-wider">NEW</span>
            </div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8720C] mb-4">OG Playlist Promotion</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
              {isEs ? '¿Tu música ya está en Spotify?' : '¿Your music already on Spotify?'} <span className="gold-text">{isEs ? 'Llévala a más oyentes.' : 'Reach more listeners.'}</span>
            </h2>
            <p className="text-lg text-white/60 mb-3">
              {isEs ? 'Incluimos tu track en nuestras 6 playlists curadas OG MUSIC. Pega tu URL de Spotify, elige plan y nuestro equipo se encarga del resto.' : 'We add your track to our 6 curated OG MUSIC playlists. Paste your Spotify URL, pick a plan and our team handles the rest.'}
            </p>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 ring-1 ring-green-500/30 text-green-300 text-xs font-bold">
              <CheckCircle className="w-3.5 h-3.5" /> {isEs ? 'Garantía de inclusión · Reporte de streams incluido' : 'Inclusion guarantee · Streams report included'}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-10">
            {promoPlans.map((p) => {
              const isPopular = p.popular || p.id === 'pro';
              const features = (p.features_es || []).slice(0, 6);
              const eff = p.effective_price ?? p.price;
              const hasPromo = p.promo_active && p.promo_discount_pct > 0;
              return (
              <Link
                key={p.id}
                to={`/playlist-promotion?plan=${p.id}`}
                className={`relative glass-card p-7 hover:-translate-y-1 transition-all ${isPopular ? 'border-[#E8720C]/40 ring-1 ring-[#E8720C]/20 bg-gradient-to-br from-[#E8720C]/5 to-transparent' : 'border-white/10'}`}
                data-testid={`home-promo-plan-${p.id}`}
              >
                {isPopular && (
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#E8720C] to-[#C55A00] text-white rounded-full">
                    {isEs ? 'Más popular' : 'Most popular'}
                  </span>
                )}
                {hasPromo && (
                  <span className="absolute top-4 right-4 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-red-500 text-white rounded-full animate-pulse">
                    {p.promo_label_es || `-${p.promo_discount_pct}%`}
                  </span>
                )}
                <h3 className="text-xl font-bold mb-1">{p.name}</h3>
                <div className="flex items-baseline gap-2 mb-5 flex-wrap">
                  <span className="text-3xl font-bold gold-text">${eff.toFixed(2)}</span>
                  {hasPromo && <span className="text-sm text-white/40 line-through">${p.price.toFixed(2)}</span>}
                  <span className="text-xs text-white/40">/ track</span>
                </div>
                <ul className="space-y-2 mb-5">
                  {features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white/70">
                      <CheckCircle className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${isPopular ? 'text-[#E8720C]' : 'text-green-400/80'}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className={`w-full py-2.5 text-center rounded-lg text-sm font-bold ${isPopular ? 'bg-[#E8720C] hover:bg-[#C55A00] text-white' : 'bg-white/5 hover:bg-white/10 text-white border border-white/10'} transition-colors`}>
                  {isEs ? 'Empezar' : 'Start'}
                </div>
              </Link>
            );})}
          </div>

          <div className="text-center">
            <Link to="/playlist-promotion" className="inline-flex items-center gap-2 text-[#E8720C] hover:text-white text-sm font-semibold transition-colors">
              {isEs ? 'Ver todos los detalles y nuestras +100 playlists curadas' : 'See full details and our +100 curated playlists'} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>


      {/* PACKS SECTION */}
      <section id="packs" className="section-padding bg-gradient-to-b from-transparent via-[#0a0a0c] to-transparent">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-4">
                {t('packs.overline')}
              </p>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
                {t('packs.title')} <span className="gold-text">{t('packs.titleHighlight')}</span>
              </h2>
              <p className="text-white/50 mt-2">Curated packs from $25 to $100</p>
            </div>
            <Link 
              to="/packs" 
              className="mt-6 md:mt-0 flex items-center gap-2 text-white/60 hover:text-white transition-colors"
              data-testid="view-all-packs"
            >
              {t('packs.viewAll')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {packs.slice(0, 3).map((pack) => (
              <div 
                key={pack.id}
                className="glass-card overflow-hidden group hover:-translate-y-1 transition-transform"
                data-testid={`pack-card-${pack.id}`}
              >
                <div className="relative aspect-video overflow-hidden">
                  <img 
                    src={pack.cover_image} 
                    alt={pack.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="px-2 py-1 text-xs font-medium bg-white/10 backdrop-blur-sm rounded-full">
                      {pack.track_count} {t('packs.tracks')}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2">{pack.name}</h3>
                  <p className="text-sm text-white/50 mb-4">{pack.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold gold-text">${pack.price}</span>
                    <Link 
                      to={`/checkout?type=pack&id=${pack.id}`}
                      className="btn-secondary py-2 px-6 text-sm"
                      data-testid={`pack-buy-${pack.id}`}
                    >
                      {t('packs.buyPack')}
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ULTIMATE PACK SECTION */}
      <section id="ultimate" className="section-padding">
        <div className="container-custom">
          <div className="glass-card p-8 md:p-12 lg:p-16 border-[#E8720C]/20 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#E8720C]/10 via-transparent to-[#E8720C]/10 blur-3xl" />
            
            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block px-4 py-1 text-xs font-bold uppercase tracking-wider bg-gradient-to-r from-[#E8720C] to-[#C55A00] text-black rounded-full mb-6">
                  {t('packs.ultimateOverline')}
                </span>
                <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                  {t('packs.ultimateName')}
                </h2>
                <p className="text-lg text-white/60 mb-8">
                  {t('packs.ultimateDesc')}
                </p>
                
                <ul className="space-y-3 mb-8">
                  {(Array.isArray(t('packs.ultimateFeatures')) ? t('packs.ultimateFeatures') : []).map((feature) => (
                    <li key={feature} className="flex items-center gap-3 text-white/70">
                      <CheckCircle className="w-5 h-5 text-[#E8720C]" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="flex items-baseline gap-3 mb-8">
                  <span className="text-5xl font-bold gold-text">$149.99</span>
                  <span className="text-white/40">one-time</span>
                </div>

                <button 
                  onClick={() => navigate('/checkout?type=pack&id=ultimate')}
                  className="btn-gold text-lg px-12"
                  data-testid="ultimate-pack-buy"
                >
                  {t('packs.getUltimate')}
                </button>
              </div>

              <div className="relative">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/54d51791-75da-4508-8099-24af4d7cd2f0/images/70801011375dc03e65ba02286b51ef6663a7d0b6de4f742712ea5093fda2b635.png"
                  alt="Ultimate Pack"
                  className="w-full rounded-2xl animate-float"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ARTIST LAUNCH SYSTEM SECTION */}
      <section id="artist-launch" className="section-padding bg-gradient-to-b from-transparent via-[#0a0a0c] to-transparent">
        <div className="container-custom">
          <div className="glass-card p-8 md:p-12 border-white/10 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0A1128]/50 via-transparent to-[#E8720C]/20 blur-3xl" />
            
            <div className="relative">
              <div className="text-center mb-12">
                <span className="inline-flex items-center gap-2 px-4 py-1 text-xs font-bold uppercase tracking-wider bg-white text-black rounded-full mb-6">
                  <Rocket className="w-3 h-3" />
                  {t('artistLaunch.overline')}
                </span>
                <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                  {t('artistLaunch.name')}
                </h2>
                <p className="text-lg text-white/60 max-w-2xl mx-auto">
                  {t('artistLaunch.description')}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                {/* Music Package */}
                <div className="glass-card p-6">
                  <Music className="w-10 h-10 text-[#E8720C] mb-4" />
                  <h3 className="text-xl font-semibold mb-4">Music Package</h3>
                  <ul className="space-y-2">
                    {(Array.isArray(t('artistLaunch.features')) ? t('artistLaunch.features') : []).map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-white/70">
                        <CheckCircle className="w-4 h-4 text-[#E8720C] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Distribution Process */}
                <div className="glass-card p-6">
                  <BarChart3 className="w-10 h-10 text-[#E8720C] mb-4" />
                  <h3 className="text-xl font-semibold mb-4">{t('artistLaunch.distribution')}</h3>
                  <ul className="space-y-2">
                    {(Array.isArray(t('artistLaunch.distFeatures')) ? t('artistLaunch.distFeatures') : []).map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-white/70">
                        <CheckCircle className="w-4 h-4 text-[#E8720C] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Playlist Ecosystem */}
                <div className="glass-card p-6">
                  <Eye className="w-10 h-10 text-[#E8720C] mb-4" />
                  <h3 className="text-xl font-semibold mb-4">{t('artistLaunch.playlistTitle')}</h3>
                  <ul className="space-y-2">
                    {(Array.isArray(t('artistLaunch.playlistFeatures')) ? t('artistLaunch.playlistFeatures') : []).map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-white/70">
                        <CheckCircle className="w-4 h-4 text-[#E8720C] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="text-center">
                <div className="flex items-baseline justify-center gap-3 mb-6">
                  <span className="text-5xl font-bold gold-text">$249.99</span>
                  <span className="text-white/40">one-time</span>
                </div>
                <button 
                  onClick={() => navigate('/checkout?type=pack&id=artist-launch')}
                  className="btn-primary text-lg px-12"
                  data-testid="artist-launch-buy"
                >
                  <Rocket className="w-5 h-5 mr-2 inline" />
                  {t('artistLaunch.cta')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MEMBERSHIP SECTION */}
      <section id="membership" className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-4">
              {t('membership.overline')}
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
              {t('membership.title')} <span className="gold-text">{t('membership.titleHighlight')}</span>
            </h2>
            <p className="text-lg text-white/60 max-w-2xl mx-auto">
              {t('membership.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {membershipTiers.map((tier) => (
              <div 
                key={tier.id}
                className={`glass-card p-8 relative ${tier.exclusive ? 'bg-black border-[#E8720C]/30 ring-1 ring-[#E8720C]/20' : ''} ${tier.popular ? 'ring-1 ring-white/20' : ''} ${tier.bestEntry ? 'ring-2 ring-[#E8720C]/40 bg-[#E8720C]/5' : ''}`}
                data-testid={`membership-card-${tier.id}`}
              >
                {tier.bestEntry && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 text-xs font-bold uppercase tracking-wider bg-[#E8720C] text-white rounded-full flex items-center gap-1">
                      <Zap className="w-3 h-3" /> Best Start
                    </span>
                  </div>
                )}
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 text-xs font-bold uppercase tracking-wider bg-white text-black rounded-full">
                      {t('membership.bestValue')}
                    </span>
                  </div>
                )}
                {tier.exclusive && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 text-xs font-bold uppercase tracking-wider bg-gradient-to-r from-[#E8720C] to-[#C55A00] text-black rounded-full flex items-center gap-1">
                      <Crown className="w-3 h-3" /> {t('membership.premium')}
                    </span>
                  </div>
                )}
                <div className="text-center mb-8 pt-4">
                  <h3 className={`text-2xl font-semibold mb-2 ${tier.exclusive ? 'gold-text' : ''}`}>
                    {tier.name}
                  </h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className={`text-5xl font-bold ${tier.exclusive ? 'gold-text' : ''}`}>
                      ${tier.price}
                    </span>
                    <span className="text-white/40">{t('membership.perMonth')}</span>
                  </div>
                  <p className="text-sm text-white/50 mt-3">
                    {tier.downloads === -1 ? t('membership.unlimited') : `${tier.downloads} ${t('membership.downloadsPerMonth')}`}
                  </p>
                </div>
                <ul className="space-y-4 mb-8">
                  {Array.isArray(tier.features) && tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-white/70">
                      <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${tier.exclusive ? 'text-[#E8720C]' : 'text-white/50'}`} />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button 
                  onClick={() => navigate(`/checkout?type=membership&id=${tier.id}`)}
                  className={`w-full py-4 rounded-full font-semibold transition-all ${
                    tier.exclusive ? 'btn-gold' : tier.popular ? 'btn-primary' : tier.bestEntry ? 'bg-[#E8720C] hover:bg-[#FF8C2E] text-white' : 'btn-secondary'
                  }`}
                  data-testid={`membership-join-${tier.id}`}
                >
                  {t('membership.join')} {tier.name}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROMOTION SECTION */}
      <section id="promotion" className="section-padding relative overflow-hidden bg-gradient-to-b from-transparent via-[#0a0a0c] to-transparent">
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-4">
                {t('promotion.overline')}
              </p>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
                {t('promotion.title')} <span className="gold-text">{t('promotion.titleHighlight')}</span>
              </h2>
              <p className="text-lg text-white/60">
                {t('promotion.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="glass-card p-6 text-center">
                <Eye className="w-8 h-8 text-[#E8720C] mx-auto mb-4" />
                <h3 className="font-semibold mb-2">{t('promotion.features.exposure')}</h3>
                <p className="text-sm text-white/50">{t('promotion.features.exposureDesc')}</p>
              </div>
              <div className="glass-card p-6 text-center">
                <Users className="w-8 h-8 text-[#E8720C] mx-auto mb-4" />
                <h3 className="font-semibold mb-2">{t('promotion.features.visibility')}</h3>
                <p className="text-sm text-white/50">{t('promotion.features.visibilityDesc')}</p>
              </div>
              <div className="glass-card p-6 text-center">
                <Target className="w-8 h-8 text-[#E8720C] mx-auto mb-4" />
                <h3 className="font-semibold mb-2">{t('promotion.features.positioning')}</h3>
                <p className="text-sm text-white/50">{t('promotion.features.positioningDesc')}</p>
              </div>
            </div>

            {/* Promotion Flow */}
            <div className="glass-card p-8 mb-8">
              <h3 className="text-lg font-semibold mb-6 text-center">{t('howItWorks.title')} {t('howItWorks.titleHighlight')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-10 h-10 rounded-full bg-[#E8720C] text-black font-bold flex items-center justify-center mx-auto mb-3">1</div>
                  <p className="text-sm text-white/70">{t('promotion.flow.step1')}</p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 rounded-full bg-[#E8720C] text-black font-bold flex items-center justify-center mx-auto mb-3">2</div>
                  <p className="text-sm text-white/70">{t('promotion.flow.step2')}</p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 rounded-full bg-[#E8720C] text-black font-bold flex items-center justify-center mx-auto mb-3">3</div>
                  <p className="text-sm text-white/70">{t('promotion.flow.step3')}</p>
                </div>
              </div>
            </div>

            <div className="text-center">
              <button 
                className="btn-gold mb-4"
                data-testid="promote-release-btn"
              >
                {t('promotion.cta')}
              </button>
              <p className="text-sm text-white/40">{t('promotion.note')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES SECTION */}
      <section id="services" className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-4">
              {t('services.overline')}
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
              {t('services.title')} <span className="gold-text">{t('services.titleHighlight')}</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((service) => (
              <div 
                key={service.id}
                className="glass-card overflow-hidden group hover:-translate-y-1 transition-transform"
                data-testid={`service-card-${service.id}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2">{service.name}</h3>
                  <p className="text-sm text-white/50 mb-4">{service.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-white/40 text-sm">{t('services.startingAt')}</span>
                    <span className="text-xl font-bold gold-text">${service.price}</span>
                  </div>
                  <button 
                    className="w-full btn-secondary mt-4 py-2"
                    data-testid={`service-start-${service.id}`}
                  >
                    {t('services.startProject')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DELIVERY SECTION */}
      <section id="delivery" className="section-padding bg-gradient-to-b from-transparent via-[#0a0a0c] to-transparent">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-4">
              {t('delivery.overline')}
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
              {t('delivery.title')} <span className="gold-text">{t('delivery.titleHighlight')}</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {deliveryItems.map((item, index) => (
              <div 
                key={index}
                className="glass-card p-6 text-center"
                data-testid={`delivery-item-${index}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#E8720C]/20 to-[#E8720C]/5 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-[#E8720C]" />
                </div>
                <h3 className="font-semibold mb-1">{item.title}</h3>
                <p className="text-sm text-white/50">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST SECTION */}
      <section id="trust" className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
              {t('trust.title')} <span className="gold-text">{t('trust.titleHighlight')}</span>
            </h2>
          </div>
          
          <div className="flex justify-center items-center gap-12 flex-wrap opacity-40">
            {[1, 2, 3, 4, 5].map((i) => (
              <div 
                key={i}
                className="w-24 h-12 bg-white/5 rounded-lg flex items-center justify-center"
              >
                <Headphones className="w-8 h-8 text-white/30" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EMAIL CAPTURE SECTION */}
      <section id="subscribe" className="section-padding bg-gradient-to-b from-transparent via-[#0a0a0c] to-transparent">
        <div className="container-custom">
          <div className="glass-card max-w-2xl mx-auto p-8 md:p-12 text-center">
            <Star className="w-10 h-10 text-[#E8720C] mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">
              {t('subscribe.title')}
            </h2>
            <p className="text-white/60 mb-8">
              {t('subscribe.subtitle')}
            </p>
            
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('subscribe.placeholder')}
                className="flex-1 input-glass"
                required
                data-testid="subscribe-email-input"
              />
              <button 
                type="submit" 
                className="btn-gold whitespace-nowrap"
                data-testid="subscribe-btn"
              >
                {t('subscribe.cta')}
              </button>
            </form>
            
            {subscribeStatus === 'success' && (
              <p className="text-green-400 mt-4 text-sm">{t('subscribe.success')}</p>
            )}
            {subscribeStatus === 'error' && (
              <p className="text-red-400 mt-4 text-sm">{t('subscribe.error')}</p>
            )}
          </div>
        </div>
      </section>

      {/* NEWS SECTION */}
      <section id="news" className="section-padding">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-4">
                {t('news.overline')}
              </p>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
                {t('news.title')} <span className="gold-text">{t('news.titleHighlight')}</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {news.map((article) => {
              const CardBody = (
                <>
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 text-xs font-medium bg-white/10 backdrop-blur-sm rounded-full">
                        {article.category}
                      </span>
                    </div>
                    {article.artist_name && (
                      <div className="absolute bottom-3 left-4">
                        <span className="px-2 py-0.5 text-[10px] font-semibold bg-[#E8720C]/80 backdrop-blur-sm rounded text-white">
                          {article.artist_name}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-semibold mb-2 line-clamp-2">{article.title}</h3>
                    <p className="text-sm text-white/50 line-clamp-2">{article.excerpt}</p>
                    <div className="mt-4 text-sm text-[#E8720C] hover:text-[#E8720C]/80 transition-colors flex items-center gap-1">
                      {t('news.readMore')} <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </>
              );
              return article.link_url ? (
                <a 
                  key={article.id} href={article.link_url} target="_blank" rel="noreferrer"
                  className="glass-card overflow-hidden group hover:-translate-y-1 transition-transform block"
                  data-testid={`news-card-${article.id}`}>
                  {CardBody}
                </a>
              ) : (
                <article 
                  key={article.id}
                  className="glass-card overflow-hidden group hover:-translate-y-1 transition-transform"
                  data-testid={`news-card-${article.id}`}>
                  {CardBody}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS SECTION */}
      <section id="how-it-works" className="section-padding" data-testid="how-it-works-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#E8720C] mb-4">{t('howItWorks.overline')}</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              {t('howItWorks.title')} <span className="gold-text">{t('howItWorks.titleHighlight')}</span>
            </h2>
            <p className="text-white/50 max-w-xl mx-auto">{t('howItWorks.subtitle')}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6 max-w-5xl mx-auto">
            {[
              { step: "1", icon: Music, title: t('howItWorks.step1'), desc: t('howItWorks.step1Desc') },
              { step: "2", icon: ShoppingCart, title: t('howItWorks.step2'), desc: t('howItWorks.step2Desc') },
              { step: "3", icon: Download, title: t('howItWorks.step3'), desc: t('howItWorks.step3Desc') },
              { step: "4", icon: FileAudio, title: t('howItWorks.step4'), desc: t('howItWorks.step4Desc') },
              { step: "5", icon: Rocket, title: t('howItWorks.step5'), desc: t('howItWorks.step5Desc') },
            ].map((item, i) => (
              <div key={i} className="glass-card p-5 text-center group hover:-translate-y-1 transition-transform">
                <div className="w-12 h-12 rounded-full bg-[#E8720C] flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                  <item.icon className="w-5 h-5 text-black" />
                </div>
                <h4 className="font-semibold text-sm mb-1">{item.title}</h4>
                <p className="text-[11px] text-white/50">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/catalog" className="btn-gold text-sm px-8" data-testid="hiw-browse-btn">
              {t('howItWorks.cta')} <ArrowRight className="w-4 h-4 inline ml-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* DISTRIBUTION GUIDE BANNER */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #1a0e04 0%, #0c0c10 50%, #0e1a08 100%)', border: '1px solid rgba(232,114,12,0.25)' }}
            data-testid="guide-banner">
            <div className="flex flex-col sm:flex-row items-center gap-6 p-8 sm:p-10">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-[#E8720C]/15 flex items-center justify-center shrink-0">
                <FileAudio className="w-8 h-8 sm:w-10 sm:h-10 text-[#E8720C]" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <p className="text-[#E8720C] text-[10px] font-bold uppercase tracking-[3px] mb-1">Incluido con tu compra</p>
                <h3 className="text-xl sm:text-2xl font-bold mb-2">Guia de Distribucion Musical</h3>
                <p className="text-white/50 text-sm">Las mejores distribuidoras, precios reales, consejos profesionales y como monetizar tu musica. Disponible en tu panel al comprar cualquier track o pack.</p>
              </div>
              <div className="shrink-0">
                <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3 bg-[#E8720C] hover:bg-[#C55A00] text-white font-bold rounded-xl text-sm">
                  Crear Cuenta y Acceder <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ACTIVITY METRICS / SOCIAL PROOF */}
      <section className="section-padding bg-gradient-to-b from-transparent via-[#0a0a0c] to-transparent" data-testid="activity-metrics-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              { value: `${tracks.length > 0 ? tracks.length * 12 : 150}+`, label: t('metrics.tracksAvailable'), sub: t('metrics.tracksReady') },
              { value: "50+", label: t('metrics.activeArtists'), sub: t('metrics.onPlatforms') },
              { value: "20+", label: t('metrics.curatedPlaylists'), sub: t('metrics.spotifyEcosystem') },
              { value: "15+", label: t('metrics.countriesReached'), sub: t('metrics.globalDistribution') },
            ].map((stat, i) => (
              <div key={i} className="glass-card p-6 text-center group hover:-translate-y-1 transition-transform" data-testid={`metric-${i}`}>
                <p className="text-3xl md:text-4xl font-bold gold-text mb-1">{stat.value}</p>
                <p className="text-sm font-semibold text-white/80">{stat.label}</p>
                <p className="text-[10px] text-white/40 mt-1">{stat.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS / TRUST SECTION */}
      <section className="section-padding" data-testid="testimonials-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#E8720C] mb-4">{t('testimonials.overline')}</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              {t('testimonials.title')} <span className="gold-text">{t('testimonials.titleHighlight')}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
            {(t('testimonials.quotes') || []).map((tq, i) => (
              <div key={i} className="glass-card p-6 relative" data-testid={`testimonial-${i}`}>
                <div className="text-[#E8720C] text-4xl font-serif leading-none mb-3">"</div>
                <p className="text-sm text-white/60 leading-relaxed mb-4">{tq.text}</p>
                <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                  <div className="w-10 h-10 rounded-full bg-[#E8720C]/10 flex items-center justify-center">
                    <span className="text-sm font-bold text-[#E8720C]">{tq.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold">{tq.name}</p>
                    <p className="text-[10px] text-white/40">{tq.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6">
            {(t('testimonials.trustBadges') || []).map((phrase, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-white/50">
                <CheckCircle className="w-4 h-4 text-[#E8720C]" />
                <span>{phrase}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OFFICIAL SPOTIFY + YOUTUBE PRESENCE */}
      <OfficialPresence />

      {/* FEATURED ARTISTS SECTION */}
      <FeaturedArtists compact={true} />

      {/* PROMO POPUP */}
      {showPromoPopup && promoPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4" data-testid="promo-popup"
          onClick={() => { setShowPromoPopup(false); sessionStorage.setItem('promo_dismissed', '1'); }}>
          <div className="relative w-full max-w-lg overflow-hidden"
            onClick={e => e.stopPropagation()}>
            {/* Main card */}
            <div className="relative bg-[#0a0a0c] border border-[#E8720C]/20 rounded-none p-0" style={{ boxShadow: '0 0 80px rgba(232,114,12,0.08)' }}>
              {/* Top accent line */}
              <div className="h-[2px] bg-[#E8720C]" />

              <div className="px-10 pt-10 pb-8">
                {/* OG MUSIC logo - blended into background */}
                <div className="flex items-center justify-center gap-2.5 mb-8">
                  <img src="https://customer-assets.emergentagent.com/job_og-catalog-pro/artifacts/qr8unnx1_logo%20ovalle%201.png"
                    alt="OG Music" className="h-9 w-9 object-contain rounded-lg brightness-90" style={{ mixBlendMode: 'lighten' }} />
                  <span className="text-xl font-bold tracking-tight">
                    <span className="text-white">OG</span>
                    <span className="gold-text ml-1">MUSIC</span>
                  </span>
                </div>

                {/* Discount number */}
                <div className="text-center mb-4">
                  <span className="text-7xl font-extrabold text-white tracking-tight">{promoPopup.discount_pct}%</span>
                  <span className="text-2xl font-bold text-[#E8720C] ml-2 uppercase">OFF</span>
                </div>

                {/* Banner text */}
                <h2 className="text-xl font-bold text-white text-center mb-2 tracking-wide">{promoPopup.title}</h2>
                <p className="text-[11px] text-white/30 text-center mb-8 uppercase tracking-[0.2em]">
                  {promoPopup.scope === 'genre' ? promoPopup.genres?.join(' / ') : promoPopup.scope === 'all' ? 'Aplica a todo el catalogo' : 'Tracks seleccionados'}
                </p>

                {/* Separator */}
                <div className="w-16 h-[1px] bg-[#E8720C]/30 mx-auto mb-8" />

                {/* CTA */}
                <div className="text-center">
                  <Link to="/catalog"
                    onClick={() => { setShowPromoPopup(false); sessionStorage.setItem('promo_dismissed', '1'); }}
                    className="inline-flex items-center gap-2 px-10 py-3.5 bg-[#E8720C] hover:bg-[#C55A00] text-white text-sm font-bold uppercase tracking-[0.15em] transition-colors">
                    Explorar Ofertas <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                <button onClick={() => { setShowPromoPopup(false); sessionStorage.setItem('promo_dismissed', '1'); }}
                  className="block mx-auto mt-5 text-[10px] text-white/20 hover:text-white/40 transition-colors uppercase tracking-[0.2em]">
                  Seguir navegando
                </button>
              </div>

              {/* Bottom accent line */}
              <div className="h-[1px] bg-gradient-to-r from-transparent via-[#E8720C]/30 to-transparent" />
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default HomePage;
